import { firstValueFrom } from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { NomObjectTypes } from 'src/app/core/constants';

import { AppState } from 'src/app/reducers';
import { FormsService } from 'src/app/core/services/forms.service';
import { LoggerService } from '@services/logger.service';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { WidgetSettingsComponent } from '../widget-settings/widget-settings.component';
import { WidgetSettingsService } from 'src/app/core/services/widget.settings.service';

import { selectUser } from 'src/app/core/reducers';

import {
  FeedSettingsService,
  PropertyViewConfig,
} from 'src/app/core/services/feed-settings.service';
import { faListCheck, faWrench } from '@fortawesome/free-solid-svg-icons';
import { FormArray, FormControl } from '@angular/forms';

const disabledPropertyUris = ['tool-view-details'];
@Component({
  selector: 'app-widget-settings-content-view-properties',
  standalone: false,
  templateUrl: './widget-settings-content-view-properties.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './widget-settings-content-view-properties.component.scss',
})
export class WidgetSettingsContentViewPropertiesComponent extends WidgetSettingsComponent {
  @Input() tab: string;

  viewProperties: any[] = []; // Typed more specifically if possible

  constructor(
    protected formService: FormsService,
    protected widgetSettings: WidgetSettingsService,
    protected cdf: ChangeDetectorRef,
    private translateService: TranslateService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
    protected feedSettings: FeedSettingsService,
  ) {
    super(formService, widgetSettings, cdf, store, logger);
  }

  override async ngOnInit(): Promise<void> {
    const user = await firstValueFrom(this.store.select(selectUser));
    const objectTypeId = this.resolveObjectTypeId();

    const feedConfig = await this.feedSettings.getFeedConfig(
      objectTypeId,
      user,
    );

    this.viewProperties = this.mapViewProperties(feedConfig);

    this.initializeFormProperties();

    super.ngOnInit();

    this.syncFormArray(feedConfig);
    this.cdf.markForCheck();
  }

  /**
   * Centralized logic to find the relevant Object Type ID
   */
  private resolveObjectTypeId(): number {
    if (this.massUpdate && this.widgets?.length) {
      return (
        this.widgets.find(
          (w) => w?.datasources?.[0]?.object_type_id !== undefined,
        )?.datasources?.[0]?.object_type_id ?? NomObjectTypes.ONLINE_FEED
      );
    }
    return (
      this.widget?.datasources?.[0]?.object_type_id ??
      NomObjectTypes.ONLINE_FEED
    );
  }

  /**
   * Maps the raw FeedConfig into the structure required by the checkbox-array control
   */
  private mapViewProperties(configs: PropertyViewConfig[]): any[] {
    const enabledRadioGroups = ['post_time_mode'];

    const isSelectable = (c: PropertyViewConfig) =>
      (c.path !== undefined &&
        c.path !== null &&
        c.path !== '' &&
        !c.isTitle &&
        !c.isRadio &&
        !disabledPropertyUris.includes(c.propertyUri)) ||
      (c.isRadio && enabledRadioGroups.includes(c.radioGroup));

    const toolsLabel = this.translateService.instant(
      'widget.settings-general.group.tools',
    );
    const propsLabel = this.translateService.instant(
      'widget.settings-general.group.properties',
    );

    return (configs ?? []).filter(isSelectable).map((c) => ({
      value: c.path,
      name: c.label,
      icon: c.icon,
      group: c.visibility?.includes('tools') ? toolsLabel : propsLabel,
      groupIcon: c.visibility?.includes('tools') ? faWrench : faListCheck,
      order: c.visibility?.includes('tools') ? 0 : 1,
    }));
  }

  /**
   * Syncs the existing widget settings or system defaults into the FormArray
   */
  private syncFormArray(feedConfig: PropertyViewConfig[]): void {
    if (!this.formGroup) return;

    const control = this.formGroup.get([
      'settings.feed.view_properties',
    ]) as FormArray;
    if (!(control instanceof FormArray)) return;

    const existingSettings = this.widget?.settings?.feed?.view_properties;
    const hasExisting =
      existingSettings &&
      (!Array.isArray(existingSettings) || existingSettings.length > 0);

    const selectedPaths = hasExisting
      ? existingSettings
      : feedConfig
          .filter(
            (c) => c.selected && !disabledPropertyUris.includes(c.propertyUri),
          )
          .map((c) => c.path);

    control.clear({ emitEvent: false });
    selectedPaths.forEach((path) =>
      control.push(new FormControl(path), { emitEvent: false }),
    );

    control.updateValueAndValidity({ emitEvent: false });
  }

  private initializeFormProperties(): void {
    this.formProperties = [
      {
        propertyName: 'settings.feed.view_properties',
        propertyLabel: this.translateService.instant(
          'widget.settings-general.label.select-metrics-and-tools-to-display',
        ),
        controlType: 'checkbox-array',
        propertyGroup: {
          key: 'widget-settings-general.group.post-card-properties',
          style: 'post-card-properties',
        },
        additionalConfig: {
          values: this.viewProperties,
          enableSelectAll: true,
          groupBy: 'group',
          showGroupHeaders: true,
          enableSelectAllPerGroup: true,
        },
        shouldDisplay: () => this.massUpdate || this.widget?.chart?.id !== 6215,
        isMassUpdate: true,
      },
    ];
  }
}
